import { WORD_ADDIN_AUDIENCE, type Product, type ProductConfig } from "../config";
import { BackendError, type FetchLike, WrongProductError } from "./types";

interface RequestOptions {
  method: "GET" | "POST" | "DELETE";
  body?: unknown;
  token?: string;
  /** Path prefix on `apiBase`; defaults to the product's Word add-in `routePrefix`. */
  prefix?: string;
}

function messageFrom(payload: unknown, status: number): string {
  if (payload && typeof payload === "object") {
    const p = payload as { error?: { message?: unknown }; detail?: unknown; message?: unknown };
    const candidate = p.error?.message ?? p.detail ?? p.message;
    if (typeof candidate === "string" && candidate) return candidate;
  }
  return `Request failed (${status}).`;
}

/** JSON request to one product's API. Always `credentials: 'omit'` and `cache: 'no-store'` (spec §6.1). */
export async function requestJson<T>(fetchImpl: FetchLike, config: ProductConfig, path: string, options: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.token !== undefined) headers.Authorization = `Bearer ${options.token}`;
  const response = await fetchImpl(`${config.apiBase}${options.prefix ?? config.routePrefix}${path}`, {
    method: options.method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    credentials: "omit",
    cache: "no-store",
    mode: "cors",
    redirect: "error",
    referrerPolicy: "no-referrer",
  });
  if (response.status === 204) return undefined as T;
  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }
  if (!response.ok) {
    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new BackendError(messageFrom(payload, response.status), response.status, Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : null);
  }
  return payload as T;
}

function decodeBase64Url(part: string): string {
  const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Refuse a token that is not a Word add-in token for `product` (spec §6.2). This reads the claims without
 * verifying the signature — the server verifies; this only stops the pane from ever sending one product's
 * token to the other product's API.
 */
export function assertTokenFor(product: Product, token: string): void {
  const parts = token.split(".");
  if (parts.length !== 3) throw new WrongProductError("That is not a Word sign-in token.");
  let claims: { aud?: unknown; product?: unknown };
  try {
    claims = JSON.parse(decodeBase64Url(parts[1] as string)) as { aud?: unknown; product?: unknown };
  } catch {
    throw new WrongProductError("That is not a Word sign-in token.");
  }
  const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
  if (!audiences.includes(WORD_ADDIN_AUDIENCE)) throw new WrongProductError("That token is not for the Word add-in.");
  if (claims.product !== product) throw new WrongProductError(`That token belongs to another Jurisio product, not ${product}.`);
}
